// Platform Settings — PSD §10.4 (F10.4). Settings screen.
// Multi-entity + white-label branding.

import { useState, useEffect } from 'react';
import { useStore } from '../../store/selectors';
import {
  getBranding,
  setBranding,
  resetBranding,
  type BrandingConfig,
} from '../../branding/whiteLabel';
import { useAlert } from '@ascend/alert';

export function PlatformSection() {
  const settings = useStore((s) => s.settings);
  const setDefaults = useStore((s) => s.setDefaults);
  const { confirm, alert } = useAlert();
  const [branding, setLocalBranding] = useState<BrandingConfig>(getBranding());
  const [entityName, setEntityName] = useState('');

  useEffect(() => {
    setLocalBranding(getBranding());
  }, []);

  const applyBranding = (patch: Partial<BrandingConfig>) => {
    const next = { ...branding, ...patch };
    setLocalBranding(next);
    setBranding(patch);
  };

  const handleResetBranding = async () => {
    const ok = await confirm({
      title: 'Reset branding?',
      message:
        'Display name, logo URL, accent color, and footer copy will revert to defaults.',
      confirmLabel: 'Reset',
      danger: true,
      type: 'warning',
    });
    if (!ok) return;
    resetBranding();
    setLocalBranding(getBranding());
    await alert({
      title: 'Branding reset',
      message: 'White-label branding has been restored to defaults.',
      type: 'success',
    });
  };

  return (
    <div className="settings-section">
      <h3>Multi-Entity</h3>
      <div className="form-group">
        <label htmlFor="entity-name">Active Entity</label>
        <input
          id="entity-name"
          type="text"
          value={entityName}
          onChange={(e) => setEntityName(e.target.value)}
          placeholder={settings.activeEntityName ?? '(none)'}
        />
        <button
          className="btn btn-secondary mt-2"
          onClick={() => setDefaults({ activeEntityName: entityName || null })}
        >
          Set Active Entity
        </button>
      </div>

      <h3 className="mt-4">White-Label Branding</h3>
      <div className="form-group">
        <label htmlFor="wl-name">Display Name</label>
        <input
          id="wl-name"
          type="text"
          value={branding.entityName}
          onChange={(e) => applyBranding({ entityName: e.target.value })}
        />
      </div>
      <div className="form-group">
        <label htmlFor="wl-logo">Logo URL</label>
        <input
          id="wl-logo"
          type="text"
          value={branding.logoUrl ?? ''}
          onChange={(e) => applyBranding({ logoUrl: e.target.value || null })}
          placeholder="https://..."
        />
      </div>
      <div className="form-group">
        <label htmlFor="wl-accent">Accent Color</label>
        <input
          id="wl-accent"
          type="color"
          value={branding.accentColor}
          onChange={(e) => applyBranding({ accentColor: e.target.value })}
        />
      </div>
      <div className="form-group">
        <label htmlFor="wl-footer">Footer Copy</label>
        <textarea
          id="wl-footer"
          rows={2}
          value={branding.footerCopy}
          onChange={(e) => applyBranding({ footerCopy: e.target.value })}
        />
      </div>
      <button className="btn btn-ghost" onClick={handleResetBranding}>
        Reset to Defaults
      </button>
    </div>
  );
}
